import { useEffect, useState, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  InputAdornment,
  Chip,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { useSnackbar } from '../../components/SnackbarProvider';
import axiosInstance from '../../lib/axios';
import type {
  Expense,
  CreateExpenseDto,
  UpdateExpenseDto,
  ExpenseCategory,
  PaymentMethod,
  RecurringFrequency,
  Location,
  Supplier,
  User,
} from '../../types';
import {
  ExpenseCategoryLabels,
  PaymentMethodLabels,
  RecurringFrequencyLabels,
} from '../../types';

interface ExpenseFormModalProps {
  open: boolean;
  onClose: () => void;
  expense?: Expense | null;
}

interface FormData {
  category: ExpenseCategory;
  amount: number;
  expenseDate: string;
  description: string;
  locationId: string;
  supplierId: string;
  staffId: string;
  paymentMethod: PaymentMethod;
  referenceNumber: string;
  isRecurring: boolean;
  recurringFrequency: RecurringFrequency | '';
}

const defaultValues: FormData = {
  category: 'consumable',
  amount: 0,
  expenseDate: new Date().toISOString().split('T')[0],
  description: '',
  locationId: '',
  supplierId: '',
  staffId: '',
  paymentMethod: 'cash',
  referenceNumber: '',
  isRecurring: false,
  recurringFrequency: '',
};

const fetchLocations = async (): Promise<Location[]> => {
  const response = await axiosInstance.get('/locations');
  // Handle paginated response: { data: Location[], total: number }
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchSuppliers = async (): Promise<Supplier[]> => {
  const response = await axiosInstance.get('/suppliers', { params: { isActive: true, limit: 100 } });
  return Array.isArray(response.data) ? response.data : response.data.items || [];
};

const fetchStaff = async (): Promise<User[]> => {
  const response = await axiosInstance.get('/users/staff', { params: { limit: 100 } });
  // Handle paginated response format: { data: User[], total: number }
  return Array.isArray(response.data) ? response.data : response.data.data || response.data.items || [];
};

export const ExpenseFormModal = ({ open, onClose, expense }: ExpenseFormModalProps) => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const isEditing = !!expense;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues,
  });

  const category = watch('category');
  const isRecurring = watch('isRecurring');

  // Fetch dropdown data
  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocations,
    enabled: open,
  });

  const { data: suppliers } = useQuery({
    queryKey: ['suppliers-active'],
    queryFn: fetchSuppliers,
    enabled: open && category === 'restock',
  });

  const { data: staff } = useQuery({
    queryKey: ['staff'],
    queryFn: fetchStaff,
    enabled: open && category === 'salary',
  });

  useEffect(() => {
    if (expense) {
      reset({
        category: expense.category,
        amount: expense.amount,
        expenseDate: expense.expenseDate.split('T')[0],
        description: expense.description || '',
        locationId: expense.locationId || '',
        supplierId: expense.supplierId || '',
        staffId: expense.staffId || '',
        paymentMethod: expense.paymentMethod,
        referenceNumber: expense.referenceNumber || '',
        isRecurring: expense.isRecurring,
        recurringFrequency: expense.recurringFrequency || '',
      });
    } else {
      reset(defaultValues);
    }
  }, [expense, reset]);

  const createMutation = useMutation({
    mutationFn: async (data: CreateExpenseDto) => {
      const response = await axiosInstance.post('/expenses', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      showMessage('Dépense créée avec succès', 'success');
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la création';
      showMessage(message, 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateExpenseDto) => {
      const response = await axiosInstance.patch(`/expenses/${expense?.id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      showMessage('Dépense mise à jour avec succès', 'success');
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la mise à jour';
      showMessage(message, 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await axiosInstance.delete(`/expenses/${expense?.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      showMessage('Dépense supprimée avec succès', 'success');
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la suppression';
      showMessage(message, 'error');
    },
  });

  const uploadReceiptMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('receipt', file);
      const response = await axiosInstance.post(`/expenses/${expense?.id}/receipts`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      showMessage('Reçu ajouté avec succès', 'success');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors du téléchargement';
      showMessage(message, 'error');
    },
  });

  const deleteReceiptMutation = useMutation({
    mutationFn: async (receiptId: string) => {
      await axiosInstance.delete(`/expenses/${expense?.id}/receipts/${receiptId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      showMessage('Reçu supprimé', 'success');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la suppression du reçu';
      showMessage(message, 'error');
    },
  });

  const onSubmit = async (data: FormData) => {
    // Build the DTO with only relevant fields
    const dto: CreateExpenseDto = {
      category: data.category,
      amount: data.amount,
      expenseDate: data.expenseDate,
      description: data.description || undefined,
      locationId: data.locationId || undefined,
      paymentMethod: data.paymentMethod,
      referenceNumber: data.referenceNumber || undefined,
      isRecurring: data.isRecurring,
      recurringFrequency: data.isRecurring && data.recurringFrequency ? data.recurringFrequency : undefined,
    };

    // Category-specific fields
    if (data.category === 'restock' && data.supplierId) {
      dto.supplierId = data.supplierId;
    }
    if (data.category === 'salary' && data.staffId) {
      dto.staffId = data.staffId;
    }

    if (isEditing) {
      await updateMutation.mutateAsync(dto as UpdateExpenseDto);
    } else {
      await createMutation.mutateAsync(dto);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Voulez-vous vraiment supprimer cette dépense ?')) {
      await deleteMutation.mutateAsync();
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && expense) {
      setUploadingReceipt(true);
      try {
        await uploadReceiptMutation.mutateAsync(file);
      } finally {
        setUploadingReceipt(false);
      }
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteReceipt = async (receiptId: string) => {
    if (window.confirm('Supprimer ce reçu ?')) {
      await deleteReceiptMutation.mutateAsync(receiptId);
    }
  };

  const isLoading =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    uploadReceiptMutation.isPending ||
    deleteReceiptMutation.isPending;

  // Check if expense was auto-created from restock
  const isAutoCreatedRestock = expense?.category === 'restock' && !!expense?.inventoryMovementId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {isEditing ? 'Modifier la dépense' : 'Nouvelle dépense'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          {isAutoCreatedRestock && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Cette dépense a été créée automatiquement lors d'un réapprovisionnement.
              Certains champs ne peuvent pas être modifiés.
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Category Selection */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="category"
                control={control}
                rules={{ required: 'La catégorie est requise' }}
                render={({ field }) => (
                  <FormControl fullWidth required disabled={isAutoCreatedRestock}>
                    <InputLabel>Catégorie</InputLabel>
                    <Select {...field} label="Catégorie" disabled={isLoading || isAutoCreatedRestock}>
                      {Object.entries(ExpenseCategoryLabels).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Amount */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="amount"
                control={control}
                rules={{
                  required: 'Le montant est requis',
                  min: { value: 0.01, message: 'Le montant doit être positif' },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Montant"
                    type="number"
                    fullWidth
                    required
                    error={!!errors.amount}
                    helperText={errors.amount?.message}
                    disabled={isLoading || isAutoCreatedRestock}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">DZD</InputAdornment>,
                    }}
                    inputProps={{ step: '0.01', min: '0' }}
                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                  />
                )}
              />
            </Grid>

            {/* Date */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="expenseDate"
                control={control}
                rules={{ required: 'La date est requise' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Date"
                    type="date"
                    fullWidth
                    required
                    error={!!errors.expenseDate}
                    helperText={errors.expenseDate?.message}
                    disabled={isLoading}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            {/* Payment Method */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="paymentMethod"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Mode de paiement</InputLabel>
                    <Select {...field} label="Mode de paiement" disabled={isLoading}>
                      {Object.entries(PaymentMethodLabels).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Location (optional) */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="locationId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Emplacement</InputLabel>
                    <Select {...field} label="Emplacement" disabled={isLoading}>
                      <MenuItem value="">
                        <em>Organisation entière</em>
                      </MenuItem>
                      {locations?.map((loc) => (
                        <MenuItem key={loc.id} value={loc.id}>
                          {loc.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Reference Number */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="referenceNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="N° de référence / facture"
                    fullWidth
                    disabled={isLoading}
                    placeholder="FAC-2026-001"
                  />
                )}
              />
            </Grid>

            {/* Supplier (for RESTOCK category) */}
            {category === 'restock' && (
              <Grid item xs={12} sm={6}>
                <Controller
                  name="supplierId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Fournisseur</InputLabel>
                      <Select
                        {...field}
                        label="Fournisseur"
                        disabled={isLoading || isAutoCreatedRestock}
                      >
                        <MenuItem value="">
                          <em>Non spécifié</em>
                        </MenuItem>
                        {suppliers?.map((sup) => (
                          <MenuItem key={sup.id} value={sup.id}>
                            {sup.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            )}

            {/* Staff (for SALARY category) */}
            {category === 'salary' && (
              <Grid item xs={12} sm={6}>
                <Controller
                  name="staffId"
                  control={control}
                  rules={{ required: category === 'salary' ? 'L\'employé est requis' : false }}
                  render={({ field }) => (
                    <FormControl fullWidth required={category === 'salary'}>
                      <InputLabel>Employé</InputLabel>
                      <Select {...field} label="Employé" disabled={isLoading}>
                        <MenuItem value="">
                          <em>Sélectionner</em>
                        </MenuItem>
                        {staff?.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.firstName} {user.lastName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            )}

            {/* Description */}
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description"
                    fullWidth
                    multiline
                    rows={2}
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>

            {/* Recurring */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="isRecurring"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={field.onChange}
                        disabled={isLoading}
                      />
                    }
                    label="Dépense récurrente"
                  />
                )}
              />
            </Grid>

            {isRecurring && (
              <Grid item xs={12} sm={6}>
                <Controller
                  name="recurringFrequency"
                  control={control}
                  rules={{ required: isRecurring ? 'La fréquence est requise' : false }}
                  render={({ field }) => (
                    <FormControl fullWidth required={isRecurring}>
                      <InputLabel>Fréquence</InputLabel>
                      <Select {...field} label="Fréquence" disabled={isLoading}>
                        {Object.entries(RecurringFrequencyLabels).map(([value, label]) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            )}

            {/* Receipts (only in edit mode) */}
            {isEditing && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                  Reçus / Justificatifs
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {expense?.receipts?.map((receipt) => (
                    <Chip
                      key={receipt.id}
                      label={receipt.fileName}
                      onDelete={() => handleDeleteReceipt(receipt.id)}
                      deleteIcon={<DeleteIcon />}
                      variant="outlined"
                      sx={{ maxWidth: 200 }}
                    />
                  ))}
                  {(!expense?.receipts || expense.receipts.length === 0) && (
                    <Typography variant="caption" color="text.secondary">
                      Aucun reçu attaché
                    </Typography>
                  )}
                </Box>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*,application/pdf"
                  style={{ display: 'none' }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={uploadingReceipt ? <CircularProgress size={16} /> : <AttachFileIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingReceipt}
                    size="small"
                  >
                    Ajouter fichier
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CameraAltIcon />}
                    onClick={() => {
                      // Trigger file input with capture attribute
                      if (fileInputRef.current) {
                        fileInputRef.current.setAttribute('capture', 'environment');
                        fileInputRef.current.click();
                        fileInputRef.current.removeAttribute('capture');
                      }
                    }}
                    disabled={uploadingReceipt}
                    size="small"
                  >
                    Prendre photo
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          {isEditing && !isAutoCreatedRestock && (
            <Button
              color="error"
              onClick={handleDelete}
              disabled={isLoading}
              sx={{ mr: 'auto' }}
            >
              Supprimer
            </Button>
          )}
          <Button onClick={onClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
          >
            {isEditing ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
